import { useState } from "react";
import { AlertTriangle, CheckCircle2, FileDown, Gavel, Send, XCircle } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { API, api, fmtTime, fmtUSD, fmtVal, withPersona, exportGuard, errMsg } from "@/lib/api";
import { EvidenceChip, PolicyBadge } from "@/components/meridian/Badges";
import { LoanLimits } from "./LoanLimits";

const TONE = { Approve: "text-emerald-700 border-emerald-500/50 bg-emerald-500/10", "Approve with conditions": "text-amber-700 border-amber-500/50 bg-amber-500/10", Decline: "text-red-700 border-red-500/50 bg-red-500/10", "Refer back": "text-slate-700 border-slate-300" };
const RES = { Pass: ["text-emerald-700", CheckCircle2], Breach: ["text-red-700", XCircle], Condition: ["text-amber-700", AlertTriangle], "Insufficient data": ["text-slate-600", AlertTriangle] };
const MFMT = { ltv: "pct", dscr: "x", debt_yield: "pct", min_down_dscr: "x", top_tenant_share: "pct", open_liens: "num" };

function Section({ title, children, right, id }) {
  return <div className="panel" data-testid={id}><div className="px-4 py-3 border-b border-slate-200 flex justify-between items-center"><span className="eyebrow">{title}</span>{right}</div>{children}</div>;
}

function DecisionForm({ task, reload, sysrec }) {
  const [decision, setDecision] = useState("");
  const [rationale, setRationale] = useState("");
  const { personaLabel, persona } = useApp();
  const submit = async () => {
    if (!decision || !rationale.trim()) return toast.error("Choose a decision and enter a rationale");
    try { await api.post(`/tasks/${task.id}/decision`, { decision, rationale, reviewer: personaLabel }); } catch { return; }
    setRationale(""); setDecision(""); await reload(); toast.success("Decision recorded with evidence package hash");
  };
  return (
    <Section id="committee-decision" title="Human credit committee decision">
      <div className="p-4 space-y-3">
        <div className="flex flex-wrap gap-2">
          {["Approve", "Approve with conditions", "Decline", "Refer back"].map((d) => (
            <button key={d} data-testid={`decision-option-${d.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setDecision(d)} className={`text-xs px-3 py-1.5 rounded border transition-colors ${decision === d ? TONE[d] : "border-slate-300 text-slate-600 hover:text-slate-900"}`}>{d}</button>
          ))}
        </div>
        {decision && sysrec && decision !== sysrec && <div className="text-[11px] text-amber-700" data-testid="override-warning">This differs from the system recommendation ({sysrec}). It will be recorded as an override, and a rationale is required.</div>}
        <textarea data-testid="decision-rationale" value={rationale} onChange={(e) => setRationale(e.target.value)} rows={3} placeholder="Rationale for the record…" className="w-full bg-slate-50 border border-slate-300 rounded px-3 py-2 text-sm outline-none focus:border-sky-500/60" />
        <div className="flex gap-2 items-center">
          <div data-testid="decision-reviewer" className="flex-1 text-xs text-slate-600">Signing as <span className="text-slate-900 font-medium">{personaLabel}</span></div>
          <a data-testid="committee-pack-export" onClick={exportGuard(persona, "pack.export", `/tasks/${task.id}/committee-pack.pdf`)} href={withPersona(`${API}/tasks/${task.id}/committee-pack.pdf`)} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 border border-sky-500/50 text-sky-700 hover:bg-sky-600/10 text-xs px-3 py-2 rounded transition-colors"><FileDown className="w-3.5 h-3.5" />Committee pack PDF</a>
          <button data-testid="decision-submit" onClick={submit} className="flex items-center gap-1.5 bg-sky-600 hover:bg-sky-700 text-white text-xs font-medium px-4 py-2 rounded transition-colors"><Gavel className="w-3.5 h-3.5" />Record decision</button>
        </div>
        {task.decisions.map((d) => (
          <div key={d.id} className="text-xs border-t border-slate-200 pt-2" data-testid={`decision-record-${d.id}`}>
            <span className={`px-1.5 py-0.5 rounded border text-[10px] ${TONE[d.decision]}`}>{d.decision}</span>
            {d.is_override && <span className="ml-2 text-[10px] text-amber-700">OVERRIDE of system ({d.system_recommendation})</span>}
            <span className="ml-2 text-slate-600">{d.reviewer} · {fmtTime(d.at)}</span>
            <div className="text-slate-700 mt-1">{d.rationale}</div>
            <div className="num text-[10px] text-slate-400 mt-0.5">{d.evidence_package_id} · sha256 {d.evidence_sha256.slice(0, 16)}…</div>
          </div>
        ))}
      </div>
    </Section>
  );
}

function Insufficient({ r, task }) {
  const labels = Object.fromEntries((task.data_quality?.missing || []).map((m) => [m.key, m.label]));
  return (
    <div className="p-6 space-y-4" data-testid="recommendation-insufficient">
      <div className="panel p-5">
        <div className="eyebrow">System recommendation · rules-based, preliminary</div>
        <span data-testid="system-decision" className="inline-block mt-2 text-sm font-semibold px-2.5 py-1 rounded border text-slate-700 border-slate-300 bg-slate-50">Insufficient data</span>
        <p className="text-xs text-slate-600 mt-3">{r.disclaimer}</p>
        <div className="mt-3 flex flex-wrap gap-1.5">{r.missing.map((k) => <span key={k} className="chip border-red-200 bg-red-50 text-red-700" data-testid={`missing-${k}`}>{labels[k] || k.replace(/_/g, " ")}</span>)}</div>
        <p className="text-[11px] text-slate-500 mt-3">Upload the document that contains these figures, or enter them on the Evidence tab. Analyst entries are labelled and logged.</p>
      </div>
    </div>
  );
}

function SubmitPanel({ task, reload }) {
  const dq = task.data_quality;
  const [busy, setBusy] = useState(false);
  if (!dq) return null;
  const submit = async () => {
    setBusy(true);
    try { await api.post(`/tasks/${task.id}/submit`); toast.success("Sent to credit committee - it now appears in the Approvals inbox"); await reload(); window.dispatchEvent(new Event("omnifi:approvals")); }
    catch (e) { if (e.response?.status !== 403) toast.error(errMsg(e) || "Could not submit"); }
    setBusy(false);
  };
  return (
    <div className="panel p-4 flex flex-wrap items-center gap-3" data-testid="submit-committee-panel">
      <Send className="w-4 h-4 text-sky-600" />
      <div className="flex-1 min-w-[240px] text-xs">
        {dq.submitted_at ? <span className="text-emerald-700" data-testid="submitted-status">Sent to committee by {dq.submitted_by} · {fmtTime(dq.submitted_at)}</span>
          : dq.ready ? <span className="text-slate-700">Analyst review complete. Send the file and this recommendation to the credit committee.</span>
          : dq.stale ? <span className="text-amber-700" data-testid="submit-stale">New documents were uploaded since the last extraction. Re-run extraction on the Evidence tab before sending to committee.</span>
          : <span className="text-amber-700">Resolve {[dq.missing.length && `${dq.missing.length} missing`, dq.review.length && `${dq.review.length} flagged`].filter(Boolean).join(" and ")} figure{dq.missing.length + dq.review.length > 1 ? "s" : ""} on the Evidence tab before sending to committee.</span>}
      </div>
      {!dq.submitted_at && <button data-testid="submit-committee-button" disabled={!dq.ready || busy} onClick={submit} className="btn-primary disabled:opacity-40">Send to committee</button>}
    </div>
  );
}

export function Recommendation({ task, reload }) {
  const r = task.recommendation;
  if (r?.decision === "Insufficient data") return <Insufficient r={r} task={task} />;
  if (!r?.available) return <div className="p-6 text-sm text-slate-500">{task.custom ? "Upload documents and run extraction. The recommendation engine needs NOI, value and loan terms." : "Extract facts first. The recommendation engine needs NOI, value and loan terms."}</div>;
  return (
    <div className="p-6 space-y-5" data-testid="recommendation-panel">
      <SubmitPanel task={task} reload={reload} />
      <LoanLimits task={task} reload={reload} />
      <div className="panel p-5 grid md:grid-cols-[1fr_auto] gap-4">
        <div>
          <div className="eyebrow">System recommendation · rules-based, preliminary · <span className="num text-sky-700" data-testid="reco-policy-version">policy {task.policy_version}</span></div>
          <div className="flex items-center gap-3 mt-2">
            <span data-testid="system-decision" className={`text-sm font-semibold px-2.5 py-1 rounded border ${TONE[r.decision]}`}>{r.decision}</span>
            <span className="text-xs text-slate-600">Binding constraint: <span className="text-slate-900">{r.binding_constraint}</span></span>
          </div>
          <p className="text-[11px] text-slate-500 mt-3">{r.disclaimer}</p>
        </div>
        <div className="text-right">
          <div className="eyebrow">Recommended / requested</div>
          <div className="num text-2xl text-slate-900" data-testid="recommended-amount">{fmtUSD(r.recommended_amount)}</div>
          <div className="num text-xs text-slate-500">of {fmtUSD(r.requested_amount)}</div>
        </div>
      </div>
      <div className="grid xl:grid-cols-2 gap-4">
        <Section id="loan-sizing" title="Loan sizing - why this amount">
          <table className="w-full text-xs">
            <tbody>
              {r.sizing.map((s) => (
                <tr key={s.constraint} className={`border-t border-slate-200 first:border-t-0 ${s.binding ? "bg-sky-600/5" : ""}`}>
                  <td className="px-4 py-2 text-slate-800">{s.constraint}{s.binding && <span className="ml-2 text-[10px] text-sky-700">BINDING</span>}</td>
                  <td className="px-4 py-2 num text-slate-900">{fmtUSD(s.max_loan)}</td>
                  <td className="px-4 py-2 num text-[10px] text-slate-500">{s.formula}</td>
                  <td className="px-4 py-2"><PolicyBadge id={s.policy_id} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
        <Section id="sensitivity" title="What would change the recommendation">
          <div className="p-3 space-y-1.5">{r.sensitivity.map((s) => <div key={s.driver} data-testid={`reco-sensitivity-${s.driver.toLowerCase().replace(/\s+/g, "-")}`} className="flex justify-between gap-2 text-xs text-slate-700"><span>{s.text}</span><PolicyBadge id={s.policy_id} /></div>)}</div>
        </Section>
      </div>
      <Section id="policy-checks" title="Policy validation · CRE Lending Policy v2026.1">
        <table className="w-full text-xs">
          <thead><tr className="text-left text-slate-500">{["Policy", "Requirement", "Actual", "Limit", "Result", "Evidence"].map((h) => <th key={h} className="px-4 py-2 font-medium">{h}</th>)}</tr></thead>
          <tbody>
            {r.checks.map((c) => {
              const [col, Icon] = RES[c.result];
              return (
                <tr key={c.policy_id} className="border-t border-slate-200" data-testid={`policy-check-${c.policy_id}`}>
                  <td className="px-4 py-2"><PolicyBadge id={c.policy_id} /></td>
                  <td className="px-4 py-2 text-slate-700">{c.title}</td>
                  <td className="px-4 py-2 num text-slate-900">{fmtVal(c.value, MFMT[c.metric])}</td>
                  <td className="px-4 py-2 num text-slate-600">{c.op} {fmtVal(c.threshold, MFMT[c.metric])}</td>
                  <td className={`px-4 py-2 ${col}`}><span className="flex items-center gap-1"><Icon className="w-3.5 h-3.5" />{c.result}</span><div className="text-[10px] text-slate-500">{c.note}</div></td>
                  <td className="px-4 py-2"><div className="flex flex-wrap gap-1">{c.evidence.map((e) => <EvidenceChip key={e} ev={e} task={task} />)}</div></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Section>
      <div className="grid xl:grid-cols-2 gap-4">
        <Section id="reason-codes" title="Reason codes (adverse-action ready)">
          <div className="p-3 space-y-2">
            {r.reasons.map((x) => (
              <div key={x.code} className="text-xs" data-testid={`reason-${x.code}`}>
                <div className="flex gap-2 items-start"><span className="num text-[10px] text-slate-500 pt-0.5">{x.code}</span><span className="flex-1 text-slate-800">{x.text}</span><PolicyBadge id={x.policy_id} /></div>
                <div className="flex flex-wrap gap-1 ml-10 mt-1">{x.evidence.map((e) => <EvidenceChip key={e} ev={e} task={task} />)}</div>
              </div>
            ))}
          </div>
        </Section>
        <Section id="recommended-terms" title="Suggested terms & conditions">
          <div className="p-4 text-xs space-y-1.5">
            <div className="num text-slate-800">{fmtUSD(r.terms.loan_amount)} · {(r.terms.rate * 100).toFixed(2)}% fixed · {r.terms.amortization_years}-yr amortization · {r.terms.term_years}-yr term · max LTV {(r.terms.max_ltv * 100).toFixed(0)}%</div>
            <div className="num text-[11px] text-slate-500">At the recommended amount: LTV {fmtVal(r.at_recommended.ltv, "pct")} · DSCR {fmtVal(r.at_recommended.dscr, "x")} · Debt yield {fmtVal(r.at_recommended.debt_yield, "pct")}</div>
            {r.terms.covenants.map((c) => <div key={c} className="text-slate-600">· {c}</div>)}
            {r.conditions.map((c) => <div key={c.text} className="flex justify-between gap-2 text-amber-800"><span>· {c.text}</span><PolicyBadge id={c.policy_id} /></div>)}
          </div>
        </Section>
      </div>
      <DecisionForm task={task} reload={reload} sysrec={r.decision} />
    </div>
  );
}
